/**
 * Save-for-later: persists in-progress form answers in the browser's
 * localStorage (per form slug, on the forms origin) so an applicant can close
 * the page and resume on the same device. Cleared on successful submit.
 *
 * Note: file selections aren't restored (only the chosen filename is kept).
 * Cross-device resume (an emailed link) could be layered on later.
 */
import type { FormValues } from './steps.js';

export interface SavedDraft {
  values: FormValues;
  step: number;
  savedAt: string;
}

const key = (slug: string) => `jfh-form-draft:${slug}`;

export function loadDraft(slug: string): SavedDraft | null {
  try {
    const raw = localStorage.getItem(key(slug));
    if (!raw) return null;
    const d = JSON.parse(raw) as SavedDraft;
    return d && typeof d === 'object' && d.values ? d : null;
  } catch {
    return null;
  }
}

export function saveDraft(slug: string, draft: SavedDraft): void {
  try {
    localStorage.setItem(key(slug), JSON.stringify(draft));
  } catch {
    /* storage unavailable / full — saving is best-effort */
  }
}

export function clearDraft(slug: string): void {
  try {
    localStorage.removeItem(key(slug));
  } catch {
    /* ignore */
  }
}

/** Whether a draft has any meaningful applicant input worth resuming. */
export function draftHasContent(d: SavedDraft | null): boolean {
  if (!d) return false;
  if (d.step > 0) return true;
  return Object.values(d.values).some((v) => {
    if (v === undefined || v === null || v === false) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.values(v).some(Boolean);
    return true;
  });
}
